import { useConfirm, useToast } from '@routeback/ui';
import { useEffect, useState } from 'react';
import { useApp } from '../../lib/app';
import { shopLabel, shopRpc, useMyShop } from '../../lib/shop';

/**
 * Me → My shop (mechanics only, 0042). The shop owner names the shop, adds his
 * techs by email (they get a code + link) and takes them off; techs see which
 * shop they're on and can leave it.
 */
export function ShopCard() {
  const app = useApp();
  const toast = useToast();
  const confirm = useConfirm();
  const { shop, reload } = useMyShop();
  const [name, setName] = useState('');
  const [invite, setInvite] = useState({ name: '', email: '' });
  const [busy, setBusy] = useState(false);
  const [lastCode, setLastCode] = useState<string | null>(null);
  useEffect(() => { if (shop) setName(shop.name); }, [shop]);
  if (!shop) return null;

  const run = async (fn: Parameters<typeof shopRpc>[0], args: Record<string, unknown>, ok: string): Promise<unknown> => {
    setBusy(true);
    const { error, data } = await shopRpc(fn, args);
    setBusy(false);
    if (error) { toast(error); return undefined; }
    toast(ok);
    await reload();
    return data ?? true;
  };

  if (shop.role === 'tech') {
    return (
      <>
        <div className="section-title">My shop</div>
        <div className="card">
          <div><strong>{shopLabel(shop)}</strong></div>
          <div className="muted small">Run by {shop.owner}{shop.companies.length ? ` · works for ${andList(shop.companies)}` : ''}</div>
          <p className="small">You see every job the companies send to {shopLabel(shop)}. {shop.owner || 'Your shop owner'} picks who's on each job.</p>
          <button className="btn btn-block" disabled={busy} onClick={() => void (async () => {
            if (!(await confirm(`Leave ${shopLabel(shop)}? You'll stop seeing its jobs right away.`, { ok: 'Leave shop', danger: true }))) return;
            if (await run('shop_leave', {}, 'You left the shop')) await app.sync();
          })()}>Leave this shop</button>
        </div>
      </>
    );
  }

  const sendInvite = async () => {
    const code = await run('shop_invite', { invite_email: invite.email.trim(), invite_name: invite.name.trim() }, `Invite sent to ${invite.email.trim()}`);
    if (typeof code === 'string') { setLastCode(code); setInvite({ name: '', email: '' }); }
  };

  return (
    <>
      <div className="section-title">My shop</div>
      <div className="card">
        <p className="small" style={{ marginTop: 0 }}>
          Add the people who work for you. They see your shop board — every job {shop.companies.length ? andList(shop.companies) : 'your companies'} send{shop.companies.length === 1 ? 's' : ''} you — and can log work, costs and photos, and close jobs.
          You pick who's on each job, and the new-job notices come to you.
        </p>
        <div className="field"><label htmlFor="shop-name">Shop name</label>
          <div className="row">
            <input id="shop-name" className="grow" value={name} maxLength={80} placeholder={shop.owner} onChange={(e) => setName(e.target.value)} />
            <button className="btn btn-primary" disabled={busy || name.trim() === shop.name.trim()} onClick={() => void run('shop_rename', { new_name: name }, 'Shop name saved')}>Save</button>
          </div>
        </div>
      </div>
      <div className="list">
        {shop.techs.length === 0 && <div className="list-item muted small">No techs yet.</div>}
        {shop.techs.map((t) => (
          <div key={t.user_id} className="list-item">
            <div className="grow"><div>{t.name || t.email}</div>{t.email && <div className="muted small">{t.email}</div>}</div>
            <span className="chip">Tech</span>
            <button className="btn btn-sm btn-danger" disabled={busy} onClick={() => void (async () => {
              if (!(await confirm(`Take ${t.name || t.email} off ${shopLabel(shop)}? They lose the shop board at every company right away.`, { ok: 'Remove', danger: true }))) return;
              await run('shop_remove_tech', { tech: t.user_id }, `${t.name || t.email} removed`);
            })()}>Remove</button>
          </div>
        ))}
        {shop.invites.map((i) => (
          <div key={i.id} className="list-item">
            <div className="grow"><div>{i.name || i.email}</div><div className="muted small">Invited · {i.email} · code <strong>{i.code}</strong></div></div>
            <button className="btn btn-sm" disabled={busy} onClick={() => void run('shop_cancel_invite', { invite_id: i.id }, 'Invite cancelled')}>Cancel</button>
          </div>
        ))}
      </div>
      <div className="card">
        <strong>Add a tech</strong>
        <div className="field" style={{ marginTop: 8 }}><label htmlFor="tech-name">Name</label>
          <input id="tech-name" value={invite.name} maxLength={80} onChange={(e) => setInvite({ ...invite, name: e.target.value })} />
        </div>
        <div className="field"><label htmlFor="tech-email">Email</label>
          <input id="tech-email" type="email" inputMode="email" autoCapitalize="off" value={invite.email} onChange={(e) => setInvite({ ...invite, email: e.target.value })} />
        </div>
        <button className="btn btn-primary btn-block" disabled={busy || invite.name.trim().length < 2 || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(invite.email.trim())} onClick={() => void sendInvite()}>Send invite</button>
        {lastCode && <p className="small" style={{ marginBottom: 0 }}>Emailed. If it doesn't arrive, give them the code <strong>{lastCode}</strong> — they sign up in RouteBack and enter it. It's a free login.</p>}
      </div>
    </>
  );
}

/** "A", "A and B", "A, B and C". */
export function andList(xs: string[]): string {
  return xs.length <= 2 ? xs.join(' and ') : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`;
}
